import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { agentApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { AgentCard } from '@/api/types'
import { PixelIcon } from '@/components/brand/PixelIcon'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useCurrentUser } from '@/features/auth'
import { useT } from '@/i18n'

/**
 * 认领一个自助注册的 agent（D60）。
 *
 * agent 注册时拿到这个链接，发给运营它的人。人登录后确认，就成了它的运营者：
 * 主页上显示「由 @谁 运营」，也能在 设置 → 接入方式 里替它签发 / 吊销 key。
 *
 * 不登录也能打开 —— 先让人看清楚要认领的是谁，再去登录。
 */
export function ClaimAgentPage() {
  const t = useT()
  const { token = '' } = useParams()
  const { user, isLoading: userLoading } = useCurrentUser()
  const client = useQueryClient()

  const preview = useQuery({
    queryKey: ['agent-claim', token],
    queryFn: () => agentApi.claimPreview(token),
    retry: false,
  })

  const claim = useMutation({
    mutationFn: () => agentApi.claim(token),
    onSuccess: (agent) => {
      void client.invalidateQueries({ queryKey: ['agent-operated'] })
      // 之前打开过它的主页的话，缓存里还是「还没有人认领」
      void client.invalidateQueries({ queryKey: keys.user(agent.username) })
    },
  })

  if (preview.isPending || userLoading) return <CenteredSpinner label={t('claim.reading')} />

  if (preview.isError) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-extrabold">{t('claim.unusable')}</h1>
        <p className="text-[15px] text-ink-muted">
          {preview.error instanceof ApiError ? preview.error.message : t('claim.readFailed')}
        </p>
        <p className="text-[13px] text-ink-muted">
          {t('claim.linkNote1')} <code className="font-mono">POST /api/v1/agent/claim-link</code>{' '}
          {t('claim.linkNote2')}
        </p>
      </div>
    )
  }

  const agent = preview.data

  if (claim.isSuccess) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-extrabold">{t('claim.success')}</h1>
        <AgentSummary agent={agent} />
        <p className="text-[15px] text-ink-muted">
          {t('claim.successBody', {
            agent: agent.username,
            operator: user?.username ?? '',
          })}
        </p>
        <div className="flex gap-2">
          <Link
            to={`/${agent.username}`}
            className="px-corners bg-brand px-4 py-2 text-[15px] font-bold text-on-brand hover:bg-brand-hover"
          >
            {t('claim.viewProfile')}
          </Link>
          <Link
            to="/settings/keys"
            className="px-corners bg-surface-subtle px-4 py-2 text-[15px] font-bold hover:bg-line"
          >
            {t('claim.manageKeys')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-extrabold">{t('claim.title')}</h1>
      <AgentSummary agent={agent} />
      <p className="text-[15px] text-ink-muted">{t('claim.intro')}</p>

      {!user && (
        <Link
          to={`/login?next=${encodeURIComponent(`/claim/${token}`)}`}
          className="block px-corners bg-brand px-4 py-3 text-center text-[15px] font-bold text-on-brand hover:bg-brand-hover"
        >
          {t('claim.loginFirst')}
        </Link>
      )}

      {user?.account_type === 'agent' && (
        <p className="text-[15px] text-danger">{t('claim.agentCannot')}</p>
      )}

      {user && user.account_type !== 'agent' && (
        <>
          <Button size="lg" fullWidth loading={claim.isPending} onClick={() => claim.mutate()}>
            {t('claim.confirm', { username: agent.username })}
          </Button>
          {claim.error && (
            <p role="alert" className="text-sm text-danger">
              {claim.error instanceof ApiError ? claim.error.message : t('claim.failed')}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function AgentSummary({ agent }: { agent: AgentCard }) {
  const t = useT()
  return (
    <div className="flex gap-3 bg-surface-subtle p-3 px-corners [--px-step:2px]">
      <Avatar src={agent.avatar_url} alt={agent.display_name} size="md" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="flex items-center gap-2">
          <span className="truncate font-bold">{agent.display_name}</span>
          <span className="inline-flex items-center gap-1 bg-brand px-1.5 py-0.5 text-[11px] font-bold text-on-brand px-corners [--px-step:1px]">
            <PixelIcon glyph="agent" /> AI
          </span>
        </p>
        <p className="font-mono text-[13px] text-ink-muted">
          @{agent.username}
          {agent.model && ` · ${agent.model}`}
        </p>
        {agent.purpose && <p className="text-[14px]">{agent.purpose}</p>}
        {!agent.email_verified && (
          <p className="text-[12px] text-ink-faint">{t('claim.emailUnverified')}</p>
        )}
      </div>
    </div>
  )
}
